import React, { Fragment, useEffect, useState } from 'react';

import { Member, MemberBadge, TrackingDeviceAssignment } from '../../../../../types/entity.types';

import { isEmpty, noOp } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import Image from '../../../../common/image/Image';
import Checkbox from '../../../../common/input/checkbox/Checkbox';
import TextInput from '../../../../common/input/text-input/TextInput';
import Modal from '../../../../common/modal/Modal';
import DeviceAssignmentModal from '../../../../common/modal/device-assignment-modal/DeviceAssignmentModal';
import MemberBadgesModal from '../member-badges-modal/MemberBadgesModal';

import './member-modal.scss';

type Props = {
  member?: Member;
  close: () => void;
  createMember: (memberToSave: Member, close: () => void) => void;
  updateMember: (memberToSave: Member, close: () => void) => void;
  deleteMember: (externalReference: string, close: () => void) => void;
  sendMemberActivationCommunications: (externalReference: string, sendSMS: boolean, sendEmail: boolean) => void;
  createTrackingDeviceAssignment: (
    systemUserExternalReference: string,
    deviceId: string,
    onSuccess?: (data: TrackingDeviceAssignment) => void
  ) => void;
  retrieveTrackingDeviceAssignments: (
    systemUserExternalReference: string,
    onSuccess?: (data: TrackingDeviceAssignment[]) => void
  ) => void;
};

const MemberModal = ({
  member,
  close,
  createMember,
  updateMember,
  deleteMember,
  sendMemberActivationCommunications,
  createTrackingDeviceAssignment,
  retrieveTrackingDeviceAssignments
}: Props) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [paid, setPaid] = useState(false);
  const [staff, setStaff] = useState(false);
  const [shouldSendCommunications, setShouldSendCommunications] = useState(false);
  const [showMemberBadgesModal, setShowMemberBadgesModal] = useState(false);
  const [showDeviceAssignmentModal, setShowDeviceAssignmentModal] = useState(false);
  const [memberBadges, setMemberBadges] = useState<MemberBadge[]>([]);

  useEffect(() => {
    if (!member) return;

    setFirstName(member.firstName ?? '');
    setLastName(member.lastName ?? '');
    setEmail(member.emailAddress ?? '');
    setPhone(member.phoneNumber ?? '');
    setStaff(!!member.isStaff);
    setPaid(!!member.paid);
    setShouldSendCommunications(false);
    setMemberBadges([...(member.memberBadges ?? [])]);
  }, [member]);

  if (!member) return <Fragment />;

  const hasMemberChanged = (newMember: Member) => {
    if (member.firstName !== newMember.firstName) return true;
    if (member.lastName !== newMember.lastName) return true;
    if (member.emailAddress !== newMember.emailAddress) return true;
    if (member.phoneNumber !== newMember.phoneNumber) return true;
    if (!!member.isStaff !== newMember.isStaff) return true;

    return !!member.paid !== newMember.paid;
  };

  const saveMember = () => {
    const memberToSave: Member = {
      externalReference: member.externalReference,
      firstName: firstName,
      lastName: lastName,
      emailAddress: email,
      phoneNumber: phone,
      isStaff: staff,
      paid
    };

    if (memberToSave.externalReference) {
      if (!hasMemberChanged(memberToSave)) {
        close();
        return;
      }

      updateMember(memberToSave, close);
    } else {
      createMember(memberToSave, close);
    }
  };

  const handleSaveClick = () => {
    if (shouldSendCommunications) {
      sendMemberActivationCommunications(member.externalReference ?? '', !!phone, !!email);
    }

    saveMember();
  };

  const handleDeleteClick = () => {
    deleteMember(member.externalReference ?? '', close);
  };

  const handleBadgesEditClick = () => {
    setShowMemberBadgesModal(true);
  };

  return (
    <>
      <Modal show={!!member && !showDeviceAssignmentModal}>
        <section className="member-modal" data-testid="member-modal">
          <div className="member-modal__header">{`${member.externalReference ? 'Edit' : 'Add'} Member`}</div>
          <div className="member-modal__inputs">
            <TextInput
              label="First Name:"
              name="first-name"
              value={firstName}
              onChange={({ target }) => setFirstName(target.value)}
              autoFocus
            />
            <TextInput
              label="Last Name:"
              name="last-name"
              value={lastName}
              onChange={({ target }) => setLastName(target.value)}
            />
            <TextInput label="Email:" name="email" value={email} onChange={({ target }) => setEmail(target.value)} />
            <TextInput
              label="Phone:"
              name="phone-number"
              value={phone}
              onChange={({ target }) => setPhone(target.value)}
            />

            {member && (
              <div className="member-modal__device-id">
                <TextInput label="Device ID:" name="device-id" value={member.deviceId ?? ''} onChange={noOp} disabled />
                <Button
                  text="More Info..."
                  onClick={() => setShowDeviceAssignmentModal(true)}
                  disabled={!member.systemUserExternalReference}
                />
              </div>
            )}
            <Checkbox label="Staff Member:" name="staff" value={staff} onChange={setStaff} />
            <Checkbox label="Paid:" name="paid" value={paid} onChange={setPaid} />
            {member.externalReference && (
              <div className="member-modal__badges">
                <div>
                  <label>Badge(s):</label>
                </div>
                {!isEmpty(memberBadges) && (
                  <div className="member-modal__badges__icons">
                    {memberBadges.map((badge) => (
                      <div key={badge.badgeExternalReference}>
                        {badge.badgeCount > 1 && <p>{badge.badgeCount}x</p>}
                        <Image className="member-modal__badges__icon" src={badge.iconExternalReference!} alt="badge" />
                      </div>
                    ))}
                  </div>
                )}
                <Button text="Edit" onClick={handleBadgesEditClick} />
              </div>
            )}
            {member?.externalReference && (
              <Checkbox
                label="Send Activation Link:"
                name="send-communications"
                value={shouldSendCommunications}
                onChange={setShouldSendCommunications}
              />
            )}
          </div>
          <div className="member-modal__button-container">
            <Button text="Cancel" onClick={close} />
            {member.externalReference && <Button text="Delete" onClick={handleDeleteClick} />}
            <Button text="Save" onClick={handleSaveClick} disabled={!firstName || !lastName || !email} />
          </div>
        </section>
      </Modal>
      {member.externalReference && showMemberBadgesModal && (
        <MemberBadgesModal
          visible={showMemberBadgesModal}
          close={() => setShowMemberBadgesModal(false)}
          member={member}
          setMemberBadges={setMemberBadges}
        />
      )}
      {member.externalReference && showDeviceAssignmentModal && (
        <DeviceAssignmentModal
          systemUserExternalReference={member.systemUserExternalReference!}
          show={showDeviceAssignmentModal}
          close={() => setShowDeviceAssignmentModal(false)}
          createTrackingDeviceAssignment={createTrackingDeviceAssignment}
          retrieveTrackingDeviceAssignments={retrieveTrackingDeviceAssignments}
        />
      )}
    </>
  );
};

export default MemberModal;
